import { useQueryClient } from '@tanstack/react-query';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  getHostingSitesV2QueryKey,
  getSitesListV2QueryKey,
  useHostingSiteAddV2,
  useSitesInfo,
  useSitesSso,
} from '@newfold/huapi-js';
import {
  HostingSitesV2200ItemsItem,
  SiteInfoResponseModelV2,
} from '@newfold/huapi-js/src/index.schemas';

import useAlerts from '~/components/Alerts/alertsStore';
import { invalidateMultipleQueries } from '~/components/MFEProvider/lib/HttpClient/queryClient';
import ReassignDomainConfirmation from '~/components/ReassignDomainConfirmation';
import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import useHostingNavigate from '~/hooks/useHostingNavigate';
import { canonicalizeUrl } from '~/utils/site/helpers';

import AddSiteOptions from '../components/AddSiteOptions';
import ChooseTitle from '../components/ChooseTitle';
import ConnectDomain from '../components/ConnectDomain';
import ErrorModal from '../components/ErrorModal';
import LaunchMigrationModal from '../components/LaunchMigrationModal';
import MultiStepCard from '../components/MultiStepCard';
import TransferSiteV2 from '../components/TransferSiteV2';

type AddSitePropOptions = {
  siteList?: (HostingSitesV2200ItemsItem | SiteInfoResponseModelV2)[];
  redirectSSO?: VoidFunction;
  setChosenSiteId?: (state: number) => void;
  chosenSiteId?: string | number;
  changeLoading?: boolean;
  setChangeLoading?: (state: boolean) => void;
};

const AddSite = ({
  siteList = [],
  redirectSSO = () => {},
  setChosenSiteId = () => {},
  chosenSiteId = undefined,
  changeLoading = false,
  setChangeLoading = () => {},
}: AddSitePropOptions) => {
  const { t } = useTranslation(['addSites', 'errors']);
  const [, { generateAlert }] = useAlerts();
  const navigate = useNavigate();
  const hostingNavigate = useHostingNavigate();
  const queryClient = useQueryClient();

  const { id: hostingId } = useAccount();

  const { brandFromJWT } = useBrandInfo();
  const { handleErrors } = useErrorHandler();
  const brand = brandFromJWT.toLowerCase();

  const [existingSiteInfo, setExistingSiteInfo] = useState<
    HostingSitesV2200ItemsItem | SiteInfoResponseModelV2
  >({});
  const [openLaunchModal, setOpenLaunchModal] = useState(false);

  const defaultFormData = {
    siteName: '',
    domain: '',
    path: '',
    subtype: '',
    autoGeneration: false,
    migrationId: '',
    isMigrating: false,
  };

  const localStorageKey = `/${hostingId}/sites/add`;

  const queryKeyHostingSites = getHostingSitesV2QueryKey(hostingId);
  const queryKeySites = getSitesListV2QueryKey();
  const queryKeys = useMemo(
    () => [queryKeyHostingSites, queryKeySites],
    [queryKeyHostingSites, queryKeySites],
  );

  const [formData, setFormData] = useLocalStorage(
    localStorageKey,
    defaultFormData,
  );

  const [header, setHeader] = useState('');
  const location = useLocation();
  const isTransferPage = location?.pathname?.endsWith('transfer');

  useEffect(() => {
    (!formData?.subtype || !formData?.siteName) &&
      location?.pathname !== '/sites/add' &&
      navigate('');
    // Need to comment out lint rule to ensure function only runs ONCE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryCountSiteInfo = useRef(0);

  const handleOnClose = () => {
    setExistingSiteInfo({});
  };

  const hookOptions = {
    query: {
      refetchOnWindowFocus: false,
      enabled: false,
    },
  };

  const { refetch } = useSitesSso(
    Number(chosenSiteId),
    {
      bounce_name: 'wpmigration',
    },
    hookOptions,
  );

  const retryMigration = useCallback(
    (retryFunc = () => {}) => {
      return generateAlert({
        title: t('migrateSiteOptions.onError.title'),
        description: (
          <Trans
            i18nKey="addSites:migrateSiteOptions.onError.description"
            values={{ phoneNumber: '1-888-401-4678' }}
            components={{ Link: <Link href={'tel:+18884014678'}></Link> }}
          />
        ),
        severity: 'error',
        showCloseBtn: true,
        ctaText: t('migrateSiteOptions.onError.ctaText'),
        cta: retryFunc,
        autoHideDuration: 10000,
      });
    },
    [generateAlert, t],
  );

  const launchMigration = useCallback(() => {
    setChangeLoading(false);
    setOpenLaunchModal(true);
  }, [setChangeLoading]);

  const redirectToWPMigrationUrl: any = async () => {
    const wordpressSsoLoadingPage =
      brand === 'bluehost'
        ? `${process.env.PUBLIC_URL}/loading/wordpress-${brand}.html`
        : `${process.env.PUBLIC_URL}/loading/wordpress.html`;

    // Actually open the window
    var ssoWindow = window.open(wordpressSsoLoadingPage, '_blank');
    setChangeLoading(true);

    const { data, error } = await refetch();

    if (error) {
      // close the window
      ssoWindow?.close();
      setChangeLoading(false);
      return retryMigration(() => redirectToWPMigrationUrl());
    }
    const url = data?.data?.sso;

    if (url && ssoWindow) {
      // set url for window
      ssoWindow.location = url;

      setChangeLoading(false);
      setOpenLaunchModal(false);
      hostingNavigate();
      return;
    }

    ssoWindow?.close();

    generateAlert({
      severity: 'error',
      description: t('ssoLink.notFound'),
      showCloseBtn: true,
    });
    setChangeLoading(false);
    setOpenLaunchModal(false);
  };

  const {
    data: siteInfo,
    refetch: refetchSiteInfo,
    isSuccess: isSuccessSiteInfo,
  } = useSitesInfo(Number(chosenSiteId), {
    query: {
      gcTime: 0,
      enabled: false,
    },
  });

  useEffect(() => {
    if (isSuccessSiteInfo && changeLoading && chosenSiteId) {
      (async () => {
        if (siteInfo?.data?.status === 'active') {
          await invalidateMultipleQueries(queryClient, queryKeys);
          if (siteInfo?.data?.type === 'wordpress') {
            !isTransferPage ? redirectSSO() : launchMigration();
          } else {
            navigate(chosenSiteId ? `/sites/${chosenSiteId}` : '/');
          }
          return siteInfo;
        }
        // If the status is not active yet and it's a transfer page, keep refetching until it becomes active
        if (isTransferPage) {
          if (siteInfo?.data?.status === 'failed_create') {
            // TODO: resync the site call
            setChangeLoading(false);
            return retryMigration(() => refetchSiteInfo());
          }
          setTimeout(() => refetchSiteInfo(), 5000);
        } else {
          // If site is NOT active yet, retry up to 5 times in 5-10 second intervals
          // If site is still NOT active, redirect to site list page
          retryCountSiteInfo.current += 1;
          if (retryCountSiteInfo.current <= 5) {
            const delay = retryCountSiteInfo.current <= 3 ? 5000 : 10000;
            setTimeout(() => refetchSiteInfo(), delay);
          } else {
            generateAlert({
              title: t('alertCreatingSite.title'),
              description: t('alertCreatingSite.desc'),
              severity: 'info',
              showCloseBtn: true,
            });
            setChangeLoading(false);
            hostingNavigate();
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessSiteInfo, siteInfo, chosenSiteId]);

  const { mutate: createWebsite, isPending: isSiteAddPending } =
    useHostingSiteAddV2({
      mutation: {
        onMutate: () => {},
        onSuccess: async (data) => {
          setChangeLoading(true);
          await invalidateMultipleQueries(queryClient, queryKeys);
          if (formData.migrationId) {
            setTimeout(() => {
              hostingNavigate();
            }, 10000);
          } else if (formData?.isMigrating) {
            //TODO: fix the huapi schema or update the huapi resp to return id instead of site_id
            // @ts-expect-error
            setChosenSiteId(Number(data?.data?.site_id));
          } else {
            // @ts-expect-error
            setChosenSiteId(Number(data?.data?.site_id));
            // will trigger site info call to check site status after 10 seconds
            setTimeout(() => refetchSiteInfo(), 10000);
          }
          localStorage.removeItem(localStorageKey);
        },
        onError: (error: any) => {
          if (formData.migrationId) {
            generateAlert({
              severity: 'error',
              description: t('addSites:migrateSiteOptions.transferError'),
              showCloseBtn: true,
            });
          } else if (error?.response?.data?.error === 'urlInUse') {
            // TODO: Get site info from BE error response to avoid looping through site list
            const existingSite = siteList.find((site) =>
              canonicalizeUrl(site?.url!).endsWith(
                canonicalizeUrl(`://${formData.domain}/${formData.path}`),
              ),
            );
            if (existingSite) {
              setExistingSiteInfo(existingSite);
            } else {
              generateAlert({
                title: t('alert.urlInUseTitle'),
                description: t('alert.urlInUseDesc', {
                  path: canonicalizeUrl(`${formData.domain}/${formData.path}`),
                }),
                severity: 'error',
                showCloseBtn: true,
                persist: true,
              });
            }
          } else if (isTransferPage || formData?.isMigrating) {
            setChangeLoading(false);
            return retryMigration(() =>
              createWebsite({
                hostingId,
                data: {
                  subtype: 'wordpress',
                },
              }),
            );
          } else {
            handleErrors(error);
          }
          setChangeLoading(false);
        },
      },
    });

  const headingText =
    header === 'transfer' ? 'migrateSiteOptions.title' : 'addSite';

  const handleDomainReassign = () => {
    createWebsite({
      hostingId,
      data: {
        title: formData.siteName,
        domain: formData.domain,
        path: formData.path,
        subtype: formData.subtype!,
        force_vhost: true,
      },
    });
  };

  const handleCloseInstallModal = () => hostingNavigate();
  const handleCancelReassignDomain = () => navigate('connect');

  useEffect(() => {
    if (isTransferPage) {
      setHeader('transfer');
    } else {
      setHeader('addsite');
    }
  }, [isTransferPage]);

  return (
    <>
      <Container data-testid="shared-add-site-flow">
        <Stack spacing={3}>
          <Typography variant="h1" color="primary">
            {t(headingText)}
          </Typography>
          <MultiStepCard>
            <Routes>
              <Route
                index
                element={
                  <AddSiteOptions
                    formData={formData}
                    setFormData={setFormData}
                    createWebsite={createWebsite}
                    isPending={changeLoading}
                  />
                }
              />
              <Route
                path="title/*"
                element={
                  <ChooseTitle
                    formData={formData}
                    setFormData={setFormData}
                    changeLoading={changeLoading}
                  />
                }
              />
              <Route
                path="connect/*"
                element={
                  <ConnectDomain
                    formData={formData}
                    setFormData={setFormData}
                    createWebsite={createWebsite}
                    changeLoading={changeLoading}
                    isSiteAddPending={isSiteAddPending}
                  />
                }
              />
              <Route
                path="reassign/*"
                element={
                  <ReassignDomainConfirmation
                    domain={formData?.domain}
                    siteName={formData?.siteName}
                    siteSubtype={formData?.subtype}
                    loading={changeLoading}
                    isPendingAdd={isSiteAddPending}
                    showInstallationModal
                    handleCloseInstallModal={handleCloseInstallModal}
                    handleCancelBtn={handleCancelReassignDomain}
                    handleProceedBtn={handleDomainReassign}
                  />
                }
              />
              <Route
                path="transfer/*"
                element={
                  <TransferSiteV2
                    changeLoading={changeLoading}
                    refetchSiteInfo={refetchSiteInfo}
                  />
                }
              />
            </Routes>
          </MultiStepCard>
        </Stack>
      </Container>
      <ErrorModal
        open={!!existingSiteInfo?.id}
        errorTitle={t('urlTaken')}
        paragraphOne={
          <Trans
            i18nKey="addSites:alreadySiteName"
            values={{
              path: existingSiteInfo.url,
            }}
          />
        }
        paragraphTwo={
          <Trans
            i18nKey="addSites:delete"
            values={{ name: existingSiteInfo.name }}
            components={{
              Link: (
                <Link href={`/sites/${existingSiteInfo.id}/settings`}></Link>
              ),
            }}
          />
        }
        handleOnClose={handleOnClose}
      />
      <LaunchMigrationModal
        open={openLaunchModal}
        handleContinue={redirectToWPMigrationUrl}
        isPending={changeLoading}
      />
    </>
  );
};

export default AddSite;
